import { onReady } from '../../shared/utils/helpers';
import { COLLECTION_WRAP_SELECTOR, MAPBOX_TOKEN } from '../../shared/utils/variables';
import Base from '../../components/base/base';
import CookieConsent from '../../components/cookie-consent/cookie-consent';
import CollectionData from '../../components/collection-data/collection-data';
import Header from '../../components/header/header';
import KLIndex from '../../components/kl-index/kl-index';
import IndexPagination from '../../components/index-pagination/index-pagination';
import IndexFilters from '../../components/index-filters/index-filters';
import IndexMap from '../../components/index-map/index-map';
import IndexViewToggle from '../../components/index-view-toggle/index-view-toggle';
import './neighborhood-index.scss';

interface NeighborhoodIndexPageOptions {
  debug?: boolean;
  [key: string]: any;
}

class NeighborhoodIndexPage extends Base {
  // private header: Header;
  private collectionData: CollectionData | null = null;
  private pagination: IndexPagination | null = null;
  private controls: IndexFilters | null = null;
  private map: IndexMap | null = null;
  private viewToggle: IndexViewToggle | null = null;

  constructor(options: NeighborhoodIndexPageOptions = {}) {
    // Pass debug option to Base class
    super(options.debug || false);
    this.debug = options.debug || false;

    // Initialize header with same debug setting for consistent logging
    // this.header = new Header({ debug: this.debug });

    this.init();
  }

  init(): void {
    this.log('NeighborhoodIndexPage initialized');

    // Cookie consent gate. Webflow holds the Meta Pixel until this grants.
    try {
      new CookieConsent({ debug: this.debug });
      this.log('CookieConsent initialized');
    } catch (e) {
      this.log('CookieConsent init failed or skipped', e);
    }

    try {
      this.collectionData = new CollectionData({ debug: this.debug });
      this.log('CollectionData initialized');
    } catch (e) {
      this.log('CollectionData init failed or skipped', e);
    }

    new KLIndex({ debug: this.debug });

    // Initialize listings pagination (also reveals the hidden collection).
    // Neighborhood cards are `.nbrhood-item`, not the `.ml-item` default.
    try {
      this.pagination = new IndexPagination({
        debug: this.debug,
        pageSize: 9,
        itemSelector: '.nbrhood-item',
        wrapSelector: COLLECTION_WRAP_SELECTOR
      });
      this.log('IndexPagination initialized');
    } catch (e) {
      this.log('IndexPagination init failed', e);
      // Fail open: never leave the inventory hidden if pagination breaks
      document.querySelector(COLLECTION_WRAP_SELECTOR)?.classList.remove('hidden');
    }

    // Initialize the map column. A neighborhood is identified by its name, not
    // its price, so the pill and the popup's prominent line both read
    // data-neighborhood, and the price becomes a "Starting at" line beneath.
    // data-full-address is empty on these cards, so no address line is shown.
    try {
      this.map = new IndexMap({
        debug: this.debug,
        accessToken: MAPBOX_TOKEN,
        stickyUnder: '.nav',
        itemSelector: '.kl-hcard',
        wrapSelector: COLLECTION_WRAP_SELECTOR,
        labelAttribute: 'data-neighborhood',
        labelFormat: 'text',
        popupFields: [
          { attribute: 'data-status', className: 'clm-popup__status' },
          { attribute: 'data-neighborhood', className: 'clm-popup__name' },
          { attribute: 'data-price', className: 'clm-popup__address', prefix: 'Starting at ' }
        ]
      });
      this.log('IndexMap initialized');
    } catch (e) {
      this.log('IndexMap init failed', e);
    }

    // Neighborhoods sort rather than filter: one "Sort By" dropdown that
    // reorders the cards in place. Same component and styling as the listings
    // controls, with no filters configured, so no sheet or trigger is built.
    // Cards are `.nbrhood-item`; their data lives on the inner `.kl-hcard`.
    // Pagination re-windows the new order. The map is deliberately not re-fed:
    // a sort doesn't change which markers exist, and its own scan reads
    // coordinates from `.kl-hcard` rather than these outer items.
    try {
      this.controls = new IndexFilters({
        debug: this.debug,
        itemSelector: '.nbrhood-item',
        dataSelector: '.kl-hcard',
        wrapSelector: COLLECTION_WRAP_SELECTOR,
        showCount: true,
        filters: [
          {
            type: 'sort',
            label: 'Sort By',
            options: [
              { label: 'Name', attribute: 'data-neighborhood', type: 'text', direction: 'asc' },
              { label: 'Price - Low to High', attribute: 'data-price', type: 'number', direction: 'asc' },
              { label: 'Price - High to Low', attribute: 'data-price', type: 'number', direction: 'desc' },
              { label: 'Square Footage - Low to High', attribute: 'data-sq-ft', type: 'number', direction: 'asc' },
              { label: 'Square Footage - High to Low', attribute: 'data-sq-ft', type: 'number', direction: 'desc' }
            ]
          }
        ],
        onChange: (ordered) => {
          this.pagination?.setItems(ordered);
        }
      });
      this.log('IndexFilters initialized');
    } catch (e) {
      this.log('IndexFilters init failed', e);
    }

    // Mobile-only List/Map toggle. The map is hidden ≤767px; this swaps the
    // card list for it on tap and activates the map (creating it on first
    // show, so phones in list view never load Mapbox).
    try {
      this.viewToggle = new IndexViewToggle({
        debug: this.debug,
        containerSelector: '.map-page-container',
        navSelector: '.nav',
        onChange: (view) => {
          if (view === 'map') this.map?.activate();
        }
      });
      this.log('IndexViewToggle initialized');
    } catch (e) {
      this.log('IndexViewToggle init failed', e);
    }
  }

  /**
   * Cleanup method
   */
  destroy(): void {
    if (this.pagination) {
      this.pagination.destroy();
    }

    if (this.controls) {
      this.controls.destroy();
    }

    if (this.map) {
      this.map.destroy();
    }

    if (this.viewToggle) {
      this.viewToggle.destroy();
    }
  }
}

onReady(() => {
  // You can enable debug mode by passing { debug: true }
  new NeighborhoodIndexPage({ debug: true });
});

export default NeighborhoodIndexPage;
